import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    categoryBannerDispatch,
    mapCategoryBannerStateToProps,
} from "@thkStore/Store/Selectors/CategoryBannerSelectors";
import parse from "html-react-parser";
import React, { FC, useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    image = "image",
    heading = "heading",
    subheading = "subheading",
    focalPoint = "focalPoint",
    textAlignment = "textAlignment",
    textColor = "textColor",
    textBackgroundColor = "textBackgroundColor",
    contentPadding = "contentPadding",
    centerTextVertically = "centerTextVertically",
    imageSide = "imageSide",
    textSide = "textSide",
    minimumHeight = "minimumHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.textColor]: string;
        [fields.textBackgroundColor]: string;
        [fields.contentPadding]: number;
        [fields.centerTextVertically]: boolean;
        [fields.imageSide]: "left" | "right";
        [fields.textSide]: "left" | "right";
        [fields.minimumHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
    };
}

const mapStateToProps = mapCategoryBannerStateToProps;
const mapDispatchToProps = categoryBannerDispatch;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CategorySplitBannerStyles {
    wrapper?: InjectableCss;
    leftPanel?: InjectableCss;
    rightPanel?: InjectableCss;
    imagePanel?: InjectableCss;
    textPanel?: InjectableCss;
    textContentWrapper?: InjectableCss;
}

export const categorySplitBannerStyles: CategorySplitBannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;

            @media (max-width: 768px) {
                flex-direction: column;
            }
        `,
    },
    leftPanel: {
        css: css`
            flex: 1;
            display: flex;

            @media (max-width: 768px) {
                width: 100%;
                min-height: 200px;
            }
        `,
    },
    rightPanel: {
        css: css`
            flex: 1;
            display: flex;

            @media (max-width: 768px) {
                width: 100%;
            }
        `,
    },
    imagePanel: {
        css: css`
            width: 100%;
            height: 100%;
            background-repeat: no-repeat;
            background-size: cover;
            position: relative;
        `,
    },
    textPanel: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
        `,
    },
    textContentWrapper: {
        css: css`
            width: 100%;

            .category-split-banner-heading {
                font-size: 36px;
                line-height: 1;
                margin-bottom: 30px;
                position: relative;
                padding-left: 16px;
                margin-left: -4px;
                font-weight: bold;

                @media (max-width:768px){
                    margin-bottom: 24px;
                }

                &:before{
                    content: "|";
                    position: absolute;
                    left: 0;
                    top: -8px;
                    font-size: 46px;

                    @media (max-width:768px){
                        top: -12px;
                    }
                }

                p{
                    font-size: 36px;
                    font-weight: 600;
                    line-height: 1;

                    @media (max-width:768px){
                        font-size: 24px;
                    }
                }
            }

            .category-split-banner-content {
                font-size: 16px;

                @media (max-width:768px){
                    font-size: 12px;
                }
            
                p{
                    font-size: 16px;

                    @media (max-width:768px){
                        font-size: 12px;
                    }
                }
            }
        `,
    },
};

const styles = categorySplitBannerStyles;

const resolveImagePath = (raw: string): string => {
    try {
        const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
        const url = new URL(raw, base);
        return `${url.pathname}${url.search}`;
    } catch {
        return raw;
    }
};

const stripHtmlDocumentWrapper = (raw: string): string => {
    if (!raw) {
        return "";
    }
    const bodyMatch = raw.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    return bodyMatch ? bodyMatch[1].trim() : raw;
};

export const CategorySplitBanner: FC<Props> = ({
    fields: widgetFields,
    categoryId,
    category,
    parentCategoryId,
    parentCategory,
    imagePath: categoryImagePath,
    loadCategory,
}) => {
    const attemptedIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!categoryId) {
            return;
        }
        const propsLoaded = category && Object.keys(category.properties || {}).length > 0;
        if (propsLoaded) {
            return;
        }
        if (attemptedIdsRef.current.has(categoryId)) {
            return;
        }
        attemptedIdsRef.current.add(categoryId);
        loadCategory({ id: categoryId });
    }, [categoryId, category, loadCategory]);

    useEffect(() => {
        if (!parentCategoryId) {
            return;
        }
        const propsLoaded = parentCategory && Object.keys(parentCategory.properties || {}).length > 0;
        if (propsLoaded) {
            return;
        }
        if (attemptedIdsRef.current.has(parentCategoryId)) {
            return;
        }
        attemptedIdsRef.current.add(parentCategoryId);
        loadCategory({ id: parentCategoryId });
    }, [parentCategoryId, parentCategory, loadCategory]);

    const effectiveImagePath = categoryImagePath || widgetFields.image || "";
    const effectiveHeadingHtml = stripHtmlDocumentWrapper(category?.name || widgetFields.heading || "");
    const effectiveContentHtml = stripHtmlDocumentWrapper(category?.htmlContent || widgetFields.subheading || "");

    const hasImage = !!effectiveImagePath;
    const hasHeading = !!effectiveHeadingHtml;
    const hasContent = !!effectiveContentHtml;

    if (!hasImage && !hasHeading && !hasContent) {
        return null;
    }

    const backgroundStyles = effectiveImagePath
        ? `background-image: url(${resolveImagePath(effectiveImagePath)});`
        : "";
    const focalPointStyles = getFocalPointStyles(widgetFields.focalPoint);

    let minimumHeightStyles = "";
    switch (widgetFields.minimumHeight) {
        case "1/4 viewport":
            minimumHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            minimumHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            minimumHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            minimumHeightStyles = "min-height: 100vh;";
            break;
    }

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${minimumHeightStyles}

            margin-bottom: 24px;

            @media (max-width: 768px) {
                margin-bottom: 0;
            }
        `,
    };

    const imagePanelStyles = {
        css: css`
            ${styles.imagePanel?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
        `,
    };

    const textPanelStyles = {
        css: css`
            ${styles.textPanel?.css || ""}
            background-color: ${widgetFields.textBackgroundColor || "transparent"};
            color: ${widgetFields.textColor || "inherit"};
            text-align: ${widgetFields.textAlignment || "left"};
            ${
                widgetFields.centerTextVertically
                    ? `
                align-items: center;
                justify-content: center;
            `
                    : ""
            }
        `,
    };

    const textContentWrapperStyles = {
        css: css`
            ${styles.textContentWrapper?.css || ""}
            padding: ${widgetFields.contentPadding}px;

            @media (max-width: 768px) {
                padding: 36px 16px 24px;
            }
        `,
    };

    const ImagePanel = (
        <StyledWrapper
            className="category-split-banner-image-panel"
            {...imagePanelStyles}
            data-test-selector="categorySplitBanner_image"
        />
    );

    const TextPanel = (
        <StyledWrapper className="category-split-banner-text-panel" {...textPanelStyles}>
            <StyledWrapper className="category-split-banner-text-content" {...textContentWrapperStyles}>
                {hasHeading && (
                    <Typography
                        as="h1"
                        className="category-split-banner-heading"
                        data-test-selector="categorySplitBanner_heading"
                    >
                        {parse(effectiveHeadingHtml, parserOptions)}
                    </Typography>
                )}
                {hasContent && (
                    <Typography
                        className="category-split-banner-content"
                        data-test-selector="categorySplitBanner_content"
                    >
                        {parse(effectiveContentHtml, parserOptions)}
                    </Typography>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );

    const renderPanels = () => {
        if (widgetFields.imageSide === "left" && widgetFields.textSide === "right") {
            return (
                <>
                    <StyledWrapper className="category-split-banner-left" {...styles.leftPanel}>
                        {ImagePanel}
                    </StyledWrapper>
                    <StyledWrapper className="category-split-banner-right" {...styles.rightPanel}>
                        {TextPanel}
                    </StyledWrapper>
                </>
            );
        }

        if (widgetFields.imageSide === "right" && widgetFields.textSide === "left") {
            return (
                <>
                    <StyledWrapper className="category-split-banner-left" {...styles.leftPanel}>
                        {TextPanel}
                    </StyledWrapper>
                    <StyledWrapper className="category-split-banner-right" {...styles.rightPanel}>
                        {ImagePanel}
                    </StyledWrapper>
                </>
            );
        }

        if (widgetFields.imageSide === "left" && widgetFields.textSide === "left") {
            return (
                <>
                    <StyledWrapper className="category-split-banner-left" {...styles.leftPanel}>
                        <StyledWrapper
                            {...{
                                css: css`
                                    position: relative;
                                    width: 100%;
                                `,
                            }}
                        >
                            {ImagePanel}
                            <StyledWrapper
                                {...{
                                    css: css`
                                        position: absolute;
                                        top: 0;
                                        left: 0;
                                        width: 100%;
                                        height: 100%;
                                    `,
                                }}
                            >
                                {TextPanel}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper className="category-split-banner-right" {...styles.rightPanel} />
                </>
            );
        }

        return (
            <>
                <StyledWrapper className="category-split-banner-left" {...styles.leftPanel} />
                <StyledWrapper className="category-split-banner-right" {...styles.rightPanel}>
                    <StyledWrapper
                        {...{
                            css: css`
                                position: relative;
                                width: 100%;
                            `,
                        }}
                    >
                        {ImagePanel}
                        <StyledWrapper
                            {...{
                                css: css`
                                    position: absolute;
                                    top: 0;
                                    left: 0;
                                    width: 100%;
                                    height: 100%;
                                `,
                            }}
                        >
                            {TextPanel}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </>
        );
    };

    return (
        <StyledWrapper className="category-split-banner-wrapper" {...wrapperStyles}>
            {renderPanels()}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const layoutTab = {
    displayName: "Layout",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CategorySplitBanner),
    definition: {
        group: "Category Details",
        icon: "columns",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.imageSide,
                displayName: "Image/Background Side",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textSide,
                displayName: "Text Content Side",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "right",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.minimumHeight,
                displayName: "Minimum Banner Height",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textAlignment,
                displayName: "Text Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                hideEmptyOption: true,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#ffffff",
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.textBackgroundColor,
                displayName: "Text Panel Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#C51919",
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.contentPadding,
                editorTemplate: "IntegerField",
                displayName: "Content Padding",
                defaultValue: 50,
                tab: layoutTab,
            },
            {
                fieldType: "Contextual",
                name: fields.centerTextVertically,
                editorTemplate: "CheckboxField",
                displayName: "Center Text Vertically",
                defaultValue: true,
                tab: layoutTab,
            },
        ],
    },
};

export default widgetModule;
